import {ref, watch, Ref} from 'vue'
import {usePandaService} from '@/composables/panda_service'
import {useConfigurationDataStore} from '@/store/configuration-data'
import {useQuantityStore} from '@/store/quantity'
import {USER_RIGHTS_MAPPING} from '~/assets/js/constants'

interface Product {
  sku?: string
  maxPricedQty?: number
  salesAreas?: Array<{successorSku?: string}>
}

interface ProductsData {
  products?: Product[]
  searchResults?: Product[]
}

interface LoadingIndicators {
  prices: string[]
  availabilities: string[]
  successorAvailabilities: string[]
}

interface UseTablePriceAvailabilityOptions {
  products: Ref<ProductsData | null>
  hasRight: (right: string) => boolean // eslint-disable-line no-unused-vars
  isLoggedIn: Ref<boolean>
}

export function useTablePriceAvailability(options: UseTablePriceAvailabilityOptions) {
  const {products, hasRight, isLoggedIn} = options
  const quantityStore = useQuantityStore()

  const prices = ref<any[]>([])
  const availabilities = ref<any[]>([])
  const successorAvailabilities = ref<any[]>([])
  const loadingIndicators = ref<LoadingIndicators>({
    prices: [],
    availabilities: [],
    successorAvailabilities: []
  })

  async function loadPrices() {
    if (hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_PRICE)) {
      const selectedQuantity = quantityStore.getQuantity()
      let productsToRequest: Array<{sku?: string; quantity: number}> = []
      if (products.value?.products) {
        productsToRequest = products.value.products.map(p => ({
          sku: p?.sku,
          quantity: selectedQuantity || p?.maxPricedQty || 1
        }))
      } else if (products.value?.searchResults) {
        productsToRequest = products.value.searchResults.map(p => ({
          sku: p?.sku,
          quantity: selectedQuantity || p?.maxPricedQty || 1
        }))
      }
      if (productsToRequest.length === 0) {
        return
      }
      prices.value = []
      loadingIndicators.value.prices = []
      usePandaService().postPandaPricesBulk(productsToRequest).then((response: any) => {
        if (response) {
          prices.value.push(...response)
        }
      }).finally(() => {
        loadingIndicators.value.prices.push(...productsToRequest.map((s) => {return s?.sku as string}))
      })
    }
  }

  async function loadAvailabilities() {
    if (hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY)) {
      const selectedQuantity = quantityStore.getQuantity()
      const nowUtcIsoString = new Date()
      let productsToRequest: Array<{sku?: string; availabilityDate: Date; quantity: number; defaultPlant: boolean}> = []
      if (products.value?.products) {
        productsToRequest = products.value.products.map(p => ({
          sku: p?.sku,
          availabilityDate: nowUtcIsoString,
          quantity: selectedQuantity || 1,
          defaultPlant: false
        }))
      } else if (products.value?.searchResults) {
        productsToRequest = products.value.searchResults.map(p => ({
          sku: p?.sku,
          availabilityDate: nowUtcIsoString,
          quantity: selectedQuantity || 1,
          defaultPlant: false
        }))
      }

      if (productsToRequest.length === 0) {
        return
      }
      availabilities.value = []
      loadingIndicators.value.availabilities = []
      usePandaService().postPandaAvailabilitiesBulk(productsToRequest).then((response: any) => {
        if (response) {
          availabilities.value.push(...response)
        }
      }).finally(() => {
        loadingIndicators.value.availabilities.push(...productsToRequest.map((s) => {return s?.sku as string}))
      })
    }
  }

  async function loadSuccessorAvailabilities() {
    if (hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY)) {
      const nowUtcIsoString = new Date()
      const selectedQuantity = quantityStore.getQuantity()
      let productsToRequest: Array<{sku?: string; availabilityDate: Date; quantity: number}> = []
      if (products.value?.products) {
        productsToRequest = products.value.products
          .filter(p => p.salesAreas?.[0]?.successorSku)
          .map(p => ({
            sku: p.salesAreas![0].successorSku,
            availabilityDate: nowUtcIsoString,
            quantity: selectedQuantity || 1
          }))
      } else if (products.value?.searchResults) {
        productsToRequest = products.value.searchResults
          .filter(p => p.salesAreas?.[0]?.successorSku)
          .map(p => ({
            sku: p.salesAreas![0].successorSku,
            availabilityDate: nowUtcIsoString,
            quantity: selectedQuantity || 1
          }))
      }
      successorAvailabilities.value = []
      loadingIndicators.value.successorAvailabilities = []
      try {
        if (productsToRequest.length === 0) {
          return
        }
        const response = await usePandaService().postPandaAvailabilitiesBulk(productsToRequest)
        if (response) {
          successorAvailabilities.value.push(...response)
        }
      } finally {
        loadingIndicators.value.successorAvailabilities.push(
          ...productsToRequest.map((s) => {
            return s.sku as string
          })
        )
      }
    }
  }

  // Watch for products changes - equivalent to the mixin's watch
  watch(
    products,
    async () => {
      if (!isLoggedIn.value) {
        return
      }
      // nextTick equivalent - Vue 3 handles this automatically in watchers
      if (products.value && (products.value.searchResults || products.value.products)) {
        // Only auto-load prices if NOT in experimental mode
        const configurationStore = useConfigurationDataStore()
        if (!configurationStore.experimentalMode) {
          await Promise.all([loadPrices(), loadAvailabilities(), loadSuccessorAvailabilities()])
        }
      }
    },
    {immediate: false}
  )

  return {
    prices,
    availabilities,
    successorAvailabilities,
    loadingIndicators,
    loadPrices,
    loadAvailabilities,
    loadSuccessorAvailabilities
  }
}
